/* eslint-disable @backmarket/custom-rules/invariant-routes */
import { useRuntimeConfig } from '#imports'
import type { RouteLocationRaw, RouteMeta, RouteRecordRaw } from 'vue-router'

import type { RouteExistsGuard } from '@backmarket/nuxt-layer-base/plugins/routeExists'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { generateWebviewRoutes } from '~/scopes/webviews/generateWebviewRoutes'
import redirectIfWebview from '~/scopes/webviews/middleware/redirectIfWebview'
import { toWebviewRouteName } from '~/scopes/webviews/utils'

import { CAMPAIGN_SLUG, routeImpact } from './const/config.const'
import { articleBySlug } from './data/articles/data'
import { journeyFor, stopFromToken, stopToken } from './data/journeys/data'
import { RAW_MATERIALS } from './route-names'

// Home for the active locale. Single definition of "go home" shared by the
// entry redirect and the partial-path redirect.
function homeForLocale(locale: string | string[]): RouteLocationRaw {
  return { name: 'campaign-home', params: { locale } }
}

// The article path matches any two segments, so an unknown one must 404 rather than
// render an empty reader. Matching the metal too keeps one canonical URL per article.
const articleGuard: RouteExistsGuard = (to) => {
  const article = articleBySlug(String(to.params.id))

  if (!article || article.metal.slug !== to.params.metal) {
    return { status: 404, message: 'Page Not Found' }
  }

  return true
}

const componentMeta: RouteMeta = {
  key: CAMPAIGN_SLUG,
  middleware: [redirectIfWebview],
  owners: ['bot-squad-content-front'],
  cache: 'public, max-age=43200',
}

const component = () => import('~/scopes/campaign/CampaignHome.vue')

// Transparent wrapper that renders only its children — the same record type the
// locale route uses one level up. Mounting every campaign route under this one
// CAMPAIGN_SLUG parent states the prefix once instead of on every child path.
const childrenWrapper = () =>
  import('@backmarket/nuxt-layer-base/components/RouteRecordWithChildrenOnly/RouteRecordWithChildrenOnly.vue')

export default (): RouteRecordRaw[] => {
  const { FF_ENABLE_RAW_MATERIALS_CAMPAIGN_COUNTRIES = '' } =
    useRuntimeConfig().public
  const { market } = useMarketplace()

  const isEnabled = FF_ENABLE_RAW_MATERIALS_CAMPAIGN_COUNTRIES.includes(
    market.countryCode,
  )

  const rawMaterialsRoutes: RouteRecordRaw[] = [
    {
      path: CAMPAIGN_SLUG,
      component: childrenWrapper,
      meta: {
        layout: false,
        routeExists: isEnabled,
      },
      children: [
        {
          name: RAW_MATERIALS.HOME,
          path: '',
          component,
          meta: componentMeta,
        },
        // Bare impact (no type) redirects home.
        {
          path: 'impact',
          redirect: (to) => homeForLocale(to.params.locale),
        },
        // Impact: impact/:type. Articles have their own path below, so
        // `impact/:type/:id` is gone and an old link 404s.
        {
          name: RAW_MATERIALS.IMPACT,
          path: 'impact/:type',
          component,
          meta: componentMeta,
        },
        // Guided mining. Three and four segments under the static `impact`
        // segment, where the article route's two bare params can't reach.
        {
          path: 'impact/:type/guided-mining',
          redirect: (to) => ({
            name: RAW_MATERIALS.IMPACT_MINING,
            params: { ...to.params, step: 'intro' },
          }),
        },
        {
          name: RAW_MATERIALS.IMPACT_MINING,
          path: 'impact/:type/guided-mining/:step',
          component,
          meta: componentMeta,
          // Repaired here, not in the overlay: runs before render, and on SSR.
          beforeEnter: (to) => {
            const type = Array.isArray(to.params.type)
              ? to.params.type[0]
              : to.params.type
            const journey = journeyFor(routeImpact(type))
            // No journey authored: fall back to the panel, or home.
            if (!journey)
              return routeImpact(type)
                ? {
                    name: RAW_MATERIALS.IMPACT,
                    params: {
                      locale: to.params.locale,
                      type,
                    },
                  }
                : homeForLocale(to.params.locale)

            const raw = Array.isArray(to.params.step)
              ? to.params.step[0]
              : to.params.step

            // A token this journey doesn't name restarts it, at the
            // first stop's *own* token: hardcoding `intro` here would
            // loop forever against a journey that spells it otherwise.
            return stopFromToken(journey, raw)
              ? true
              : {
                  name: RAW_MATERIALS.IMPACT_MINING,
                  params: {
                    ...to.params,
                    step: stopToken(journey, 1),
                  },
                }
          },
        },
        // Metal: metal and metal/:id
        {
          name: RAW_MATERIALS.METAL,
          path: 'metal',
          component,
          meta: componentMeta,
        },
        {
          name: RAW_MATERIALS.METAL_DETAIL,
          path: 'metal/:id',
          component,
          meta: componentMeta,
        },
        // Content gallery — films and stories in one list. Cinema mode
        // is not a route: it layers via `?video=<id>` (see useModalState).
        {
          name: RAW_MATERIALS.CONTENT_GALLERY,
          path: 'content-gallery',
          component,
          meta: componentMeta,
        },
        // Superseded segment. Carries `?video=` (a name+params redirect
        // drops it) and keeps its side of the webview mirror.
        {
          path: 'video',
          redirect: (to) => ({
            name: to.meta.isWebview
              ? toWebviewRouteName(RAW_MATERIALS.CONTENT_GALLERY)
              : RAW_MATERIALS.CONTENT_GALLERY,
            params: { locale: to.params.locale },
            query: to.query,
          }),
        },
        // Take action
        {
          name: RAW_MATERIALS.TAKE_ACTIONS,
          path: 'take-action',
          component,
          meta: componentMeta,
        },
        // Article: `<metal.slug>/<article.slug>`, no impact — one story, one URL.
        // Two bare params, but vue-router ranks static segments first, so the
        // routes above still win; last here is for the reader. The param is `id`
        // because `activeDetail` reads `route.params.id` for both readers.
        {
          name: RAW_MATERIALS.ARTICLE,
          path: ':metal/:id',
          component,
          meta: {
            ...componentMeta,
            middleware: [redirectIfWebview],
            routeExists: articleGuard,
          },
        },
      ],
    },
  ]

  const webviewRawMaterialsRoutes = generateWebviewRoutes(rawMaterialsRoutes)

  return [...rawMaterialsRoutes, ...webviewRawMaterialsRoutes]
}
